// components/ApplicationStageHandler.jsx
import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Spinner, Text } from '@chakra-ui/react';
import useUserContext from "./UserContext";
import Cookies from 'js-cookie';

const ApplicationStageHandler = ({ children }) => {
    const { userDetails, loading } = useUserContext();
    const navigate = useNavigate();
    const location = useLocation();

    // Memoize login status to prevent unnecessary recalculations
    const isLoggedIn = useMemo(() => {
        return sessionStorage.getItem("isLoggedin") === "true" ||
            Cookies.get("isLoggedin") === "true" ||
            userDetails?.isLoggedin === true;
    }, [userDetails?.isLoggedin]);

    // Define public routes that don't require authentication
    const publicRoutes = useMemo(() => [
        '/', '/login', '/register', '/forgot-password', '/verify',
        '/feedback', '/reset-password', '/download-agreement',
        '/profile-summary', '/reference'
    ], []);

    // Check if current route is public
    const isPublicRoute = useMemo(() => {
        const currentPath = location.pathname;
        return publicRoutes.some(route =>
            currentPath.startsWith(route) ||
            currentPath.includes('/reset-password/') ||
            currentPath.includes('/download-agreement/') ||
            currentPath.includes('/profile-summary/') ||
            currentPath.includes('/reference/')
        );
    }, [location.pathname, publicRoutes]);

    // Define allowed routes for each user level with backward compatibility
    const getAllowedRoutes = (level, appStatus) => {
        const baseRoutes = ['/logout', '/profile-summary', '/feedback'];

        switch (level) {
            case 1: // Registration
                return [...baseRoutes, '/register'];

            case 2: // Application
                return appStatus === 1
                    ? [...baseRoutes, '/application-submitted'] // Submitted - restricted access
                    : [...baseRoutes, '/application', '/application-submitted']; // In progress - full access

            case 3: // Interview
                return [...baseRoutes, '/interview'];

            case 4: // Pre-employment
                return [...baseRoutes, '/employment-check'];

            case 5: // Onboarding - Allow access to both level 4 and 5 routes
                return [...baseRoutes, '/onboarding', '/contract-agreement', '/employment-check','/summary'];

            case 6: // Staff
                return [...baseRoutes, '/dashboard'];

            default:
                return baseRoutes;
        }
    };

    // Check if user has access to current route
    const hasAccessToRoute = (userLevel, appStatus, currentPath) => {
        const allowedRoutes = getAllowedRoutes(userLevel, appStatus);
        
        // Check if current route starts with any allowed route
        const isAllowed = allowedRoutes.some(route =>
            currentPath.startsWith(route) ||
            currentPath.includes('/profile-summary/') ||
            currentPath.includes('/download-agreement/') ||
            currentPath.includes('/reference/')
        );

        return isAllowed;
    };

    // Get appropriate redirect target based on user level
    const getRedirectTarget = (userLevel, appStatus) => {
        switch (userLevel) {
            case 1:
                return '/register';
            case 2:
                return appStatus === 1
                    ? '/application-submitted'
                    : '/application/personal-info';
            case 3:
                sessionStorage.setItem("userLevel", 3);
                Cookies.set("userLevel", 3, { expires: 1 });
                return '/interview/schedule';
            case 4:
                sessionStorage.setItem("userLevel", 4);
                Cookies.set("userLevel", 4, { expires: 1 });
                return '/employment-check/provisional-offer';
            case 5:
                sessionStorage.setItem("userLevel", 5);
                Cookies.set("userLevel", 5, { expires: 1 });
                // Level 5 users can access both onboarding and employment-check
                // Default to onboarding but allow navigation to employment-check
                return '/onboarding/checklist';
            case 6:
                return '/dashboard';
            default:
                return '/';
        }
    };

    useEffect(() => {
        if (loading) return;

        // Handle non-authenticated users
        if (!isLoggedIn) {
            if (!isPublicRoute && location.pathname !== '/login') {
                sessionStorage.clear();
                localStorage.clear();
                Cookies.remove("userData");
                Cookies.remove("uid");
                Cookies.remove("isLoggedin");
                Cookies.remove("userLevel");
                Cookies.remove("token");
                navigate('/login', { replace: true });
            }
            return;
        }

        // Wait for user details to load
        if (!userDetails) return;

        const userLevel = userDetails?.level;
        const applicationStatus = userDetails?.application_status;

        // Check if current route is allowed for user level
        const hasAccess = hasAccessToRoute(userLevel, applicationStatus, location.pathname);

        // If route is not allowed, redirect to appropriate page
        if (!hasAccess) {
            const targetRoute = getRedirectTarget(userLevel, applicationStatus);

            // Only navigate if we're not already on the target route
            if (location.pathname !== targetRoute) {
                navigate(targetRoute, { replace: true });
            }
        }

        // Additional protection: If application is submitted, block access to application routes
        if (userLevel === 2 && applicationStatus === 1 && location.pathname.startsWith('/application/')) {
            navigate('/application-submitted', { replace: true });
        }

        // Special case: Level 5 users can access employment-check routes
        // This is already handled in getAllowedRoutes, but we add explicit logging for debugging
        if (userLevel === 5 && location.pathname.startsWith('/employment-check/')) {
            console.log('Level 5 user accessing employment-check routes - allowed');
        }

    }, [userDetails, loading, isLoggedIn, isPublicRoute, location.pathname, navigate]);

    // Show loading state only for authenticated users
    if (loading && isLoggedIn) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                flexDirection="column"
                gap={4}
            >
                <Spinner size="xl" />
                <Text>Loading your application...</Text>
            </Box>
        );
    }

    // Show error state only for authenticated users who failed to load details
    if (!userDetails && isLoggedIn) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                flexDirection="column"
                gap={4}
            >
                <Text fontSize="lg" color="red.500">
                    Unable to load user details.
                </Text>
                <Text>
                    Please refresh the page or try logging in again.
                </Text>
            </Box>
        );
    }

    return children;
};

export default ApplicationStageHandler;